import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { usd } from '../api/client';
import { usePayouts, useRevenue } from '../api/hooks';
import { Badge, Card, Loading, PageHeader, Panel } from '../components/ui';

const REPORT_TIMEZONE = 'America/Chicago';

const dateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: REPORT_TIMEZONE,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

const zonedMonthStart = (year: number, monthIndex: number) => {
  const wallTime = Date.UTC(year, monthIndex, 1);
  let instant = wallTime;
  for (let iteration = 0; iteration < 3; iteration += 1) {
    const parts = dateTimeFormatter.formatToParts(new Date(instant));
    const value = (type: Intl.DateTimeFormatPartTypes) =>
      Number(parts.find((part) => part.type === type)?.value ?? 0);
    const observedWallTime = Date.UTC(
      value('year'),
      value('month') - 1,
      value('day'),
      value('hour'),
      value('minute'),
      value('second'),
    );
    instant += wallTime - observedWallTime;
  }
  return new Date(instant);
};

const monthRange = (offset = 0) => {
  const now = new Date();
  const parts = dateTimeFormatter.formatToParts(now);
  const year = Number(parts.find((part) => part.type === 'year')?.value);
  const month = Number(parts.find((part) => part.type === 'month')?.value);
  const label = new Date(Date.UTC(year, month - 1 + offset, 1));
  const from = zonedMonthStart(label.getUTCFullYear(), label.getUTCMonth());
  const nextLabel = new Date(Date.UTC(label.getUTCFullYear(), label.getUTCMonth() + 1, 1));
  const to = zonedMonthStart(nextLabel.getUTCFullYear(), nextLabel.getUTCMonth());
  return { from: from.toISOString(), to: to.toISOString(), label };
};

/** Revenue, refunds, platform fees and payout reconciliation. */
export function RevenuePage() {
  const { t, i18n } = useTranslation();
  const [offset, setOffset] = useState(0);
  const range = monthRange(offset);
  const revenue = useRevenue(range.from, range.to);
  const payouts = usePayouts();

  if (revenue.isPending) return <Loading />;
  if (revenue.isError || !revenue.data) return <Panel>{t('revenue.noAccess')}</Panel>;

  const data = revenue.data;
  const kpis = [
    { key: 'transactions', value: String(data.transactionCount) },
    { key: 'grossTotal', value: usd(data.grossCents) },
    { key: 'refunds', value: usd(data.refundTotalCents) },
    { key: 'platformFee', value: usd(data.platformFeeCents) },
    { key: 'net', value: usd(data.netCents) },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t('revenue.title', {
          month: range.label.toLocaleDateString(i18n.language, {
            year: 'numeric',
            month: 'long',
            timeZone: 'UTC',
          }),
        })}
        hint={t('revenue.hint')}
        actions={
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-lg border border-hairline px-3 py-2 text-xs"
              onClick={() => setOffset((v) => v - 1)}
            >
              {t('revenue.prevMonth')}
            </button>
            <button
              type="button"
              className="rounded-lg border border-hairline px-3 py-2 text-xs disabled:opacity-40"
              disabled={offset >= 0}
              onClick={() => setOffset((v) => Math.min(0, v + 1))}
            >
              {t('revenue.nextMonth')}
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {kpis.map((kpi) => (
          <Card key={kpi.key}>
            <p className="text-[11px] text-muted">{t(`revenue.${kpi.key}`)}</p>
            <p className="num text-xl font-semibold">{kpi.value}</p>
            <p className="text-[10px] text-faint">{t(`revenue.${kpi.key}Sub`)}</p>
          </Card>
        ))}
      </div>

      <div className="grid gap-5 lg:grid-cols-[1.15fr_0.85fr]">
        <div className="flex flex-col gap-3">
          <span className="text-xs text-muted">{t('revenue.daily')}</span>
          <div className="h-40 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data.daily.map((d) => ({ date: d.date.slice(5), gross: d.grossCents / 100 }))}
                margin={{ top: 8, right: 8, bottom: 0, left: -20 }}
              >
                <CartesianGrid strokeDasharray="2 4" stroke="#e4e3de" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#8a8a84' }} />
                <YAxis tick={{ fontSize: 10, fill: '#8a8a84' }} />
                <Tooltip formatter={(value) => `$${Number(value ?? 0).toFixed(2)}`} />
                <Bar dataKey="gross" fill="#0b0b0b" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {data.daily.length === 0 ? <Panel>{t('revenue.noTransactions')}</Panel> : null}
        </div>

        <div className="flex flex-col gap-3">
          <h3 className="text-[13px] font-bold">{t('revenue.payouts')}</h3>
          {payouts.isPending ? (
            <Loading />
          ) : payouts.data && payouts.data.payouts.length > 0 ? (
            <div className="overflow-hidden rounded-xl border border-hairline">
              {payouts.data.payouts.map((payout) => (
                <div
                  key={payout.id}
                  className="flex items-center justify-between border-b border-hairline px-3 py-2.5 text-[13px] last:border-b-0"
                >
                  <div className="flex flex-col">
                    <span className="num">
                      {new Date(payout.periodStart).toLocaleDateString(i18n.language, {
                        month: 'numeric',
                        day: 'numeric',
                      })}{' '}
                      –{' '}
                      {new Date(payout.periodEnd).toLocaleDateString(i18n.language, {
                        month: 'numeric',
                        day: 'numeric',
                      })}
                    </span>
                    <span className="text-[11px] text-faint">{payout.note ?? t('revenue.weekly')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="num font-semibold">{usd(payout.netCents)}</span>
                    <Badge tone={payout.status === 'paid' ? 'success' : 'neutral'}>
                      {t(payout.status === 'paid' ? 'revenue.paid' : 'revenue.pending')}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Panel>{t('revenue.noPayouts')}</Panel>
          )}

          <Panel>{t('revenue.panel')}</Panel>
        </div>
      </div>
    </div>
  );
}
